/**
 * 英文翻译（en）
 *
 * 所有 UI 文本的英文版。key 必须与 zh-CN.ts 严格对齐。
 */

import type zhCN from './zh-CN';

const en: Record<keyof typeof zhCN, string> = {
  // ===== Common =====
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.back": "Back",
  "common.settings": "Settings",
  "common.deploy": "Deploy",
  "common.map": "Map",
  "common.test": "Test",
  "common.build": "Build",
  "common.clear": "Clear",
  "common.unknown": "Unknown",
  "common.loading": "Loading",

  // ===== App =====
  "app.title": "BattleCode · v1.0.2-demo",

  // ===== Screen 0 Title Screen =====
  "home.title_line1": "PROJECT",
  "home.title_line2": "BLADE-BEARER",
  "home.world_bg_line1": "2019: Zhanlu System born. 2023: documents sealed, the Quenching Hypothesis persists.",
  "home.world_bg_line2": "2028: Project Blade-Bearer initiated, LLM as the new engine.",
  "home.world_bg_line3": "Forge Academy forges knowledge; Sharpen Ground tempers it in combat.",
  "home.world_bg_line4": "From single-agent decision to multi-agent emergence.",
  "home.world_bg_line5": "You inherit the edge they forged.",
  "home.poem_line1": "Bear War-Mind Legacy · Awaken Swarm Wisdom",
  "home.poem_line2": "Forge Code as Blade · Quench Tactic as Edge",
  "home.poem_line3": "Forge Not for War · Expand Wisdom Together",
  "home.poem_latin": "Ignis · Ferrum · Honor",
  "home.poem_subtext": "// Forge Chain: Blueprint → Forge → Sharpen → Bear",
  "home.entry_forge_label": "FORGE IN HALL",
  "home.entry_forge_sub": "━━ ◇ ━━",
  "home.entry_sharpen_label": "SHARPEN ON GROUND",
  "home.entry_sharpen_sub": "━━ ◇ ━━",
  "home.entry_bear_label": "BEAR THE BLADE",
  "home.entry_bear_sub": "━━ ◇ ━━",
  "home.entry_sandbox_label": "SANDTABLE WARGAME",
  "home.entry_sandbox_sub": "━━ ◇ ━━",
  "home.entry_armory_label": "ARMORY HALL",
  "home.entry_armory_sub": "━━ ◈ ━━",
  "home.nav_levels": "[Levels]",
  "home.nav_settings": "[Settings]",
  "home.footer": "Project Blade-Bearer v7.0 · Pyodide · No Backend",

  // ===== Screen 1 Project List =====
  "project.home_btn": "Home",
  "project.arena": "⚡ Arena",
  "project.sharpen_btn": "Sharpen Ground →",
  "project.settings_btn": "Settings",
  "project.my_levels": "My Levels",
  "project.arena_records": "Arena Records",
  "project.builtin_levels": "Built-in",
  "project.tutorial_levels": "Tutorials",
  "project.no_levels": "No levels yet.",
  "project.go_builtin": "Start from Built-in",
  "project.no_arena_records": "No Arena records yet.",
  "project.go_arena": "Enter Arena",
  "project.no_available": "No levels available.",
  "project.from_level": "From Level",
  "project.from_level_desc": "Select a level to create a new project. Map, AI, and template code will be auto-configured.",
  "project.no_levels_modal": "No levels available.",
  "project.or_blank": "Or create a blank project (no level)",
  "project.default_name": "My First Bot",
  "project.delete_title": "Delete Project",
  "project.delete_body": "Are you sure you want to delete this project? Code will be lost.",
  "project.unknown_level": "Unknown Level",
  "project.unknown_ai": "Unknown AI",
  "project.opponent_ai": "Opponent AI",
  "project.commits": "COMMITS",
  "project.created": "CREATED",
  "project.enter_edit": "Enter Edit",
  "project.arena_hint": "Arena records · click DEPLOY to fight champion bot",
  "project.select_level_hint": "Select level · click Enter Edit to auto-create project",
  "project.console_log_hint": "console log → prototype/logs/browser.log",
  "project.logo_sub": "",

  // ===== Screen 2 Editor =====
  "editor.back_to_list": "← Back",
  "editor.project_list": "Project List",
  "editor.deploy_btn": "DEPLOY",
  "editor.save_btn": "Save",
  "editor.console": "▌ CONSOLE",
  "editor.control_output": "Control Output",
  "editor.build_tab": "BUILD",
  "editor.test_tab": "TEST",
  "editor.deploy_tab": "DEPLOY",
  "editor.clear_btn": "Clear",
  "editor.console_placeholder": "Click BUILD to compile and view output; TEST to run preflight; DEPLOY to check dry-run. Results will appear here.",
  "editor.map_panel": "MAP",
  "editor.vibe_title": "VIBE CODING",
  "editor.vibe_subtitle": "Human-AI Collaboration",
  "editor.vibe_placeholder": "Describe in plain language · e.g. \"Fire when you see the enemy, otherwise move to center\"",
  "editor.generate_mode": "Generate Mode",
  "editor.optimize_mode": "Optimize existing code",
  "editor.overwrite_mode": "Overwrite",
  "editor.generate_btn": "Generate Code →",
  "editor.clear_vibe": "Clear",
  "editor.generate_progress": "AI Generation Progress",
  "editor.saved": "Saved",
  "editor.python_lang": "PYTHON",
  "editor.json_lang": "JSON",
  "editor.status_line": "Python 3.12 · UTF-8 · LF",
  "editor.pending_banner": "⚠ Pending",
  "editor.reject_btn": "✕ Reject",
  "editor.accept_btn": "✓ Accept",
  "editor.switch_back_tank": "Switched back to Tank.py. Continue editing or click [Generate Code] for Vibe AI.",
  "editor.cleared_msg": "Cleared. Click BUILD/TEST/DEPLOY to switch tabs.",
  "editor.syntax_ok": "Syntax check passed. Run TEST for preflight, or click [DEPLOY] to enter battle directly.",
  "editor.skip_compile": "Skipped compile: v1.1.0 multi-file support coming soon",
  "editor.switch_to_tank": "Switch to Tank.py to compile / TEST / DEPLOY.",
  "editor.test_untested": "Not tested (can deploy directly, but code correctness isn't guaranteed)",
  "editor.test_passed": "TEST passed ✓",
  "editor.test_failed": "TEST failed ✗",
  "editor.deploy_direct": "Click [DEPLOY] to enter battle directly.",
  "editor.must_switch_tank": "Preflight only runs on Tank.py (Strategy.py/Config.json are v1.1.0 placeholders). Switch to Tank.py first.",
  "editor.preflight_start": "▶ TEST: Launching preflight + auto-fix pipeline (max 3 rounds)...",
  "editor.generating": "⏳ Generating...",
  "editor.project_not_found": "Project not found",
  "editor.tank_py": "Tank.py",
  "editor.strategy_py": "Strategy.py",
  "editor.config_json": "Config.json",
  "editor.pyodide_loading": "⏳ Loading Pyodide runtime (first time ~3-5s)...",
  "editor.prompt_empty": "Prompt cannot be empty. Select a TIP template or describe your tactic in the text area.",
  "editor.generated_success": "Generation successful. Click [✓ Accept] to save, or [✕ Reject] to discard.",
  "editor.accepted_code": "Accepted. Code saved to project. Continue editing or click [TEST] to verify.",
  "editor.reverted_code": "Reverted. Generated code discarded, editor restored to previous state.",
  "editor.vibe_failed": "✗ Vibe Generate failed",
  "editor.deploy_blocked": "⚠ DEPLOY blocked when TEST fails (state machine guard)",
  "editor.back_to_editor": "← Back to Editor",
  "editor.go_battle": "→ Enter Battle",
  "editor.engine_error": "✗ Engine Error",
  "editor.pyodide_failed": "Pyodide may have failed to load (first time ~3-5s, requires CDN).",
  "editor.retry_test": "Retry [TEST]; check network if issue persists.",
  "editor.preflight_passed": "✓ Passed",
  "editor.preflight_failed": "✗ Failed",
  "editor.manual_fix": "▼ Please fix your code manually and retry TEST",
  "editor.tip_filled": "TIP filled",
  "editor.new_player": "New Player",
  "editor.prompt_cleared": "Prompt cleared",

  // ===== Screen 4 Battle =====
  "battle.give_up": "← GIVE UP",
  "battle.time": "TIME",
  "battle.tick": "TICK",
  "battle.speed": "SPD",
  "battle.live": "● LIVE",
  "battle.done": "● DONE",
  "battle.standby": "● STANDBY",
  "battle.team_a_title": "ALPHA",
  "battle.team_b_title": "BRAVO",
  "battle.tank_log": "TANK LOG",
  "battle.console_title": "▌ CONSOLE",
  "battle.console_tab_all": "ALL",
  "battle.console_tab_team_a": "TEAM_A",
  "battle.console_tab_team_b": "TEAM_B",
  "battle.console_tab_kill": "KILL",
  "battle.console_tab_error": "ERR",
  "battle.console_empty": "Waiting for battle events...",
  "battle.first_blood": "★ FIRST BLOOD",
  "battle.ready_title": "Countdown · FIGHT IN ",
  "battle.status_dead": "Dead",
  "battle.status_cooldown": "Cooling",
  "battle.status_aiming": "Aiming",
  "battle.status_moving": "Moving",
  "battle.status_idle": "Idle",
  "battle.action_idle": "Idle",
  "battle.action_move": "Move",
  "battle.action_shoot": "Shoot",
  "battle.action_scan": "Scan",
  "battle.action_move_to": "Move → ",
  "battle.give_up_title": "Give Up?",
  "battle.give_up_body": "This battle will be recorded as a loss.",
  "battle.give_up_ok": "Give Up",
  "battle.give_up_cancel": "Continue",
  "battle.expand_console": "Expand CONSOLE",
  "battle.collapse_console": "Collapse CONSOLE",
  "battle.console_lines": "lines",
  "battle.expand_detail": "Expand Details",
  "battle.collapse_detail": "Collapse Details",
  "battle.waiting": "Waiting",
  "battle.cooldown_ready": "Ready",

  // ===== Screen 4 Tooltip =====
  "tooltip.you_player": "You · Player Tank",
  "tooltip.enemy_ai": "Enemy · AI Tank",
  "tooltip.spawn": "Spawn Point",
  "tooltip.cover": "Cover",
  "tooltip.obstacle": "Obstacle",
  "tooltip.unreachable": "Unreachable",
  "tooltip.empty": "Empty",
  "tooltip.supply_zone": "Supply Zone",
  "tooltip.team_a_you": "A · You",
  "tooltip.team_b_ai": "B · AI (Aggressive)",
  "tooltip.team_a_player": "A · Player",
  "tooltip.team_b_ai_short": "B · AI",
  "tooltip.position": "Position",
  "tooltip.hp": "HP",
  "tooltip.cooldown": "Cooldown",
  "tooltip.action": "Action",
  "tooltip.range": "Range",
  "tooltip.blocks_move": "Blocks Move",
  "tooltip.blocks_vision": "Blocks Vision",
  "tooltip.damage_reduction": "Damage Reduction",
  "tooltip.enter": "Passable",
  "tooltip.blocks": "Blocked",
  "tooltip.yes": "Yes",
  "tooltip.no": "No",
  "tooltip.none": "None",
  "tooltip.at_start": "Occupied at battle start",
  "tooltip.range_chebyshev": "tiles (Chebyshev)",

  // ===== Screen 5 Result =====
  "result.victory": "Victory",
  "result.defeat": "Defeat",
  "result.draw": "Draw",
  "result.battle_result": "Battle Result",
  "result.key_events": "Key Events",
  "result.tactical_review": "Tactical Review",
  "result.live_logs": "Live Logs",
  "result.rematch": "Rematch",
  "result.modify_code": "Modify Code",
  "result.back_hub": "Back to Hub",
  "result.after_action": "After Action",
  "result.aar_analyzing": "AAR analyzing...",
  "result.aar_error": "Tactical review generation failed. You can retry or continue.",
  "result.radar_pos": "Position",
  "result.radar_prs": "Pressure",
  "result.radar_vis": "Vision",
  "result.radar_sht": "Shooting",
  "result.radar_sur": "Survival",
  "result.radar_tmp": "Tempo",
  "result.kda": "K/D/A",
  "result.total_ticks": "Total Ticks",
  "result.duration": "Duration",
  "result.final_hp": "Final HP",
  "result.final_hp_a": "Final HP (You)",
  "result.final_hp_b": "Final HP (AI)",
  "result.battle_analysis": "Battle Analysis",
  "result.player_give_up": "Player gave up",
  "result.s_tick": "t",
  "result.s_seconds": "s",
  "result.floating_panel": "Floating Panel",
  "result.mission_result": "Mission Result",
  "result.tactical_advisor": "Tactical Advisor",
  "result.tick": "TICK",
  "result.grid": "GRID",
  "result.rounds": "rounds",
  "result.no_data": "(no data)",
  "result.chat_placeholder": "Ask a follow-up...",
  "result.chat_limit_reached": "3-round limit reached. Go back and modify code to retry.",
  "result.chat_analyzing": "Tactical advisor is analyzing...",
  "result.coach_init": "Soldier, I'm CS-07 Tactical Advisor. Weakest: {weakest} ({weakestScore}); Priority: {strongest} ({strongestScore}). You have 3 rounds, ask away.",
  "result.coach_disconnected": "⚠️ Tactical advisor disconnected: {error}",
  "result.quick_prompt_lose": "Why did I lose?",
  "result.quick_prompt_fix": "Which logic should I fix first?",
  "result.quick_prompt_aggressive": "What if I play more aggressively?",

  // ===== Screen 6 Settings =====
  "settings.title": "SETTINGS",
  "settings.back_btn": "← Back",
  "settings.llm_config": "LLM Configuration",
  "settings.llm_hint": "Natural language → code generation for Screen 2 Vibe Coding",
  "settings.api_key_label": "DeepSeek-coder API Key",
  "settings.api_key_current": "Current",
  "settings.api_key_configured": "(Configured)",
  "settings.api_key_unconfigured": "(Not Configured)",
  "settings.api_key_hint": "A shared demo key is pre-configured out of the box. Paste your own key to override.",
  "settings.save_key": "Save",
  "settings.daily_usage": "Daily Usage",
  "settings.engine_tuning": "Engine Tuning (Advanced)",
  "settings.expand": "Expand",
  "settings.tick_rate": "Tick Rate",
  "settings.soft_timeout": "Soft Timeout",
  "settings.hard_timeout": "Hard Timeout",
  "settings.evals_title": "Evals",
  "settings.evals_hint": "Run N rounds vs AI opponent to evaluate bot code quality (Harness automation FR-C)",
  "settings.evals_select_level": "Select Level",
  "settings.evals_select_placeholder": "-- Select Level --",
  "settings.evals_start": "▶ Start Evals",
  "settings.evals_running": "Running",
  "settings.evals_result_title": "Results",
  "settings.evals_win_rate": "Win Rate",
  "settings.evals_avg_survival": "Avg Survival Tick",
  "settings.evals_avg_damage": "Avg Damage",
  "settings.evals_comment_header": "Comment Header",
  "settings.evals_comment_density": "Comment Density",
  "settings.evals_api_comments": "API Call Comments",
  "settings.evals_strategy": "Strategy Signature",
  "settings.evals_no_history": "No evaluation history",
  "settings.evals_cant_read": "Cannot read level code",
  "settings.evals_history": "Evaluation History",
  "settings.theme_title": "Theme",
  "settings.theme_tactical": "Tactical (Dark)",
  "settings.theme_day": "Day (Light)",
  "settings.language_title": "Language",
  "settings.language_zh": "中文",
  "settings.language_en": "English",
  "settings.about_title": "About",
  "settings.about_line1": "BattleCode Demo v1.0.0-demo",
  "settings.about_line2": "Pyodide + Web Worker + 30Hz Tick",
  "settings.about_line3": "Locked: 1 tank / 1 battle_easy / 1 ai_easy_aggressive",
  "settings.api_key_saved": "✓ Saved to localStorage (demo phase, no backend)",

  // ===== Screen 7 Map Editor =====
  "map_editor.title": "MAP EDITOR",
  "map_editor.editing": "Editing",
  "map_editor.save": "Save",
  "map_editor.save_as": "Save As",
  "map_editor.undo": "Undo",
  "map_editor.redo": "Redo",
  "map_editor.clear_all": "Clear All",
  "map_editor.export": "Export Level",
  "map_editor.spawn_p1": "Spawn P1",
  "map_editor.spawn_p2": "Spawn P2",
  "map_editor.spawn_p1_label": "P1",
  "map_editor.spawn_p2_label": "P2",

  // ===== Screen 8 Arena =====
  "arena.title": "EVOLUTION ARENA",
  "arena.idle": "Idle",
  "arena.battling": "Battling",
  "arena.mutating": "Mutating",
  "arena.stopping": "Stopping",
  "arena.done": "Done",
  "arena.ranking": "Ranking",
  "arena.battle_field": "Battlefield",
  "arena.tournament_log": "Tournament Log",
  "arena.code": "Code",
  "arena.config": "Config",
  "arena.start": "Start",
  "arena.stop": "Stop",
  "arena.diff": "Diff",
  "arena.save_level": "Save Level",
  "arena.dual_replay": "Dual Replay",
  "arena.trend": "Trend",
  "arena.start_prompt": "Click Start to begin evolution",
  "arena.battle": "Rd",
  "arena.error": "Error",
  "arena.waiting_start": "Waiting for evolution to start...",
  "arena.select_bot_hint": "Select a bot to view code",
  "arena.gen_label": "Gen {gen}/{total}",
  "arena.vs_gen0": "vs Gen0",
  "arena.winner_draw": "DRAW",
  "arena.winner_win": "{name} WIN",
  "arena.replay_btn": "▶ REPLAY",
  "arena.battle_vs": "⚡ {a} vs {b}",
  "arena.origin_seed": "SEED",
  "arena.origin_elite": "ELITE",
  "arena.origin_mutation": "MUT",
  "arena.record_format": "W{wins} L{losses} D{draws}{vsG0}",

  // ===== Screen 8 Arena Logs =====
  "arena.log_battle_pending": "[G{gen} · {num}/{total}] {a} vs {b} ...",
  "arena.log_battle_done": "[G{gen} · {num}] {a} vs {b} → {result} (T{ticks} · spec {spec})",
  "arena.log_gen_done": "[GEN {gen} DONE · {level}] Best: {best} ({bestFit}) · Avg: {avgFit} · vsG0: {vsG0}%",
  "arena.log_complete": "[COMPLETE] Champion: {name} · Fitness: {fitness} · {battles} battles in {duration}s",
  "arena.log_error": "[ERROR] {msg}",
  "arena.log_fatal": "[FATAL] {msg}",
  "arena.log_mutate_start": "[MUTATE] {name} → analyzing...",
  "arena.log_mutate_done": "[MUTATE] {name} → ✓ {note}",
  "arena.log_mutate_fail": "[MUTATE] {name} → ✗ FAILED",
  "arena.log_auto_saved": "[AUTO-SAVED] Gen{gen} champion → Arena Records",
  "arena.log_auto_save_failed": "[AUTO-SAVE FAILED] Gen{gen} champion",
  "arena.log_saved": "[SAVED] \"{name}\" → Screen 1 Arena Records (id: {id})",
  "arena.log_save_err": "[SAVE_ERR] {msg}",

  // ===== Screen 8 Arena Modals =====
  "arena.modal_trend_title": "EVOLUTION TREND",
  "arena.modal_close": "CLOSE",
  "arena.modal_dual_title": "DUAL REPLAY · GEN 0 vs GEN {last}",
  "arena.modal_dual_label_a": "GEN 0 · {name}",
  "arena.modal_dual_label_b": "GEN {last} · {name}",
  "arena.modal_play": "▶ PLAY",
  "arena.modal_pause": "❚❚ PAUSE",
  "arena.modal_reset": "↺ RESET",
  "arena.modal_diff_title": "CODE DIFF",
  "arena.modal_diff_bot_a": "BOT A:",
  "arena.modal_diff_bot_b": "BOT B:",
  "arena.modal_diff_similarity": "Similarity: {pct}%",
  "arena.modal_config_title": "ARENA CONFIG",
  "arena.modal_config_generations": "GENERATIONS (1-4)",
  "arena.modal_config_generations_hint": "Each generation corresponds to a level (Open Duel → Cover Fire → Tactical Hold → Lateral Strike)",
  "arena.modal_config_population": "POPULATION (2-4)",
  "arena.modal_config_battles": "BATTLES PER PAIR (1-4)",
  "arena.modal_config_seed_mode": "SEED MODE",
  "arena.modal_config_seed_mixed": "MIXED (builtin + LLM)",
  "arena.modal_config_seed_builtin": "AI BUILTIN",
  "arena.modal_config_seed_llm": "LLM GENERATE",
  "arena.modal_config_level_rotation": "LEVEL ROTATION",
  "arena.modal_config_cancel": "CANCEL",
  "arena.modal_config_apply": "APPLY",
  "arena.modal_save_title": "SAVE AS PLAYABLE LEVEL",
  "arena.modal_save_body": "Save champion bot <strong>{name}</strong> (Fitness: {fitness}) to Screen 1 \"Arena Records\" for future battles.",
  "arena.modal_save_level_name": "LEVEL NAME",
  "arena.modal_save_cancel": "CANCEL",
  "arena.modal_save_save": "SAVE",

  // ===== Error Messages =====
  "error.screen4_failed": "Screen 4 failed to start",
  "error.screen7_failed": "Screen 7 failed to start",
  "error.screen_render_failed": "Screen render error",

  // ===== Battle Log =====
  "battle.ai_kill_you": "AI destroyed YOU",
  "battle.you_kill_ai": "YOU destroyed AI",

  // ===== Screen 4 HP bar =====
  "battle.hit_pct": "HIT",
  "battle.mov": "MOV",
  "battle.sho": "SHO",
  "battle.dmg": "DMG",
};

export default en;
